import {
  CognateWord,
  Module,
  VideoActivityData,
  MCQActivityData,
  FillInTheBlankData,
  BuildSentenceData,
  MatchData,
  WriteActivityData,
  CelebrateData,
} from "../activityTypes";

const BASIC_PHRASES_1_VIDEO = {
  chapterId: 6426,
  fullStoryLessonId: 45790,
  youtubeId: "6p-Qyn30tPM",
  // Tune embedded prompt clips here. Times are milliseconds into the full video.
  segments: {
    lesson1Intro: { startMs: 0, endMs: 59500 },
    lesson2Intro: { startMs: 59600, endMs: 120000 },
    buenosDias: { startMs: 5440, endMs: 9500 },
    noMeGusta: { startMs: 24400, endMs: 28400 },
    tampoco: { startMs: 30640, endMs: 34800 },
    meGusta: { startMs: 41200, endMs: 45380 },
    yoQuiero: { startMs: 45480, endMs: 50800 },
    asiSiPuedoIr: { startMs: 53580, endMs: 58820 },
    dondeEsta: { startMs: 78400, endMs: 81690 },
    aquiEsta: { startMs: 92220, endMs: 93800 },
    necesito: { startMs: 17800, endMs: 24020 },
    zapatos: { startMs: 66300, endMs: 68480 },
  },
};

// =============================================================================
// Basic Phrases 1 Words
// =============================================================================

export const BASIC_PHRASES_1_WORDS: CognateWord[] = [
  {
    id: "buenos-dias",
    spanish: "Buenos días",
    english: "Good morning",
    pattern: "COMMON",
  },
  { id: "necesito", spanish: "Necesito", english: "I need", pattern: "COMMON" },
  {
    id: "yo-quiero",
    spanish: "Yo quiero",
    english: "I want",
    pattern: "COMMON",
  },
  {
    id: "me-gusta",
    spanish: "Me gusta",
    english: "I like it",
    pattern: "COMMON",
  },
  {
    id: "no-me-gusta",
    spanish: "No me gusta",
    english: "I don't like it",
    pattern: "COMMON",
  },
  {
    id: "tampoco",
    spanish: "Tampoco",
    english: "Neither / not either",
    pattern: "COMMON",
  },
  {
    id: "donde-esta",
    spanish: "¿Dónde está?",
    english: "Where is...?",
    pattern: "COMMON",
  },
  {
    id: "aqui-esta",
    spanish: "Aquí está",
    english: "Here is it",
    pattern: "COMMON",
  },
  {
    id: "no-tengo",
    spanish: "No tengo",
    english: "I don't have",
    pattern: "COMMON",
  },
];

const BASIC_PHRASES_FILL_WORDS: CognateWord[] = [
  { id: "me", spanish: "me", english: "me", pattern: "COMMON" },
  { id: "no", spanish: "no", english: "no", pattern: "COMMON" },
  { id: "si", spanish: "sí", english: "yes", pattern: "COMMON" },
  { id: "yo", spanish: "yo", english: "I", pattern: "COMMON" },
  { id: "mi", spanish: "mi", english: "my", pattern: "COMMON" },
  { id: "donde", spanish: "Dónde", english: "where", pattern: "COMMON" },
  { id: "aqui", spanish: "aquí", english: "here", pattern: "COMMON" },
  { id: "esta", spanish: "está", english: "is", pattern: "COMMON" },
  { id: "es", spanish: "es", english: "is", pattern: "COMMON" },
  { id: "estoy", spanish: "estoy", english: "I am", pattern: "COMMON" },
  { id: "tengo", spanish: "Tengo", english: "I have", pattern: "COMMON" },
  { id: "quiero", spanish: "Quiero", english: "I want", pattern: "COMMON" },
];

const BASIC_PHRASES_LISTEN_WORDS: CognateWord[] = [
  {
    id: "asi-si-puedo-ir-a-la-fiesta",
    spanish: "Así sí puedo ir a la fiesta",
    english: "This way, yes, I can go to the party",
    pattern: "COMMON",
  },
];

const createWriteActivity = (
  id: string,
  question: string,
  answer: string,
): WriteActivityData => ({
  id,
  type: "write",
  question,
  answer,
  approved: true,
});

// =============================================================================
// Module 4 Activities (Basic Phrases 1 -- La Fiesta)
// Split into two backend lessons while preserving the original practice beats.
// =============================================================================

const m4Step1_Video: VideoActivityData = {
  id: "m4-step1-video",
  type: "video",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  continueButtonLabel: "Continue",
  videoTutorialMode: "combined_word_phrase",
  phraseHighlightTutorialTargetPhrase: "cambiarme de ropa",
  defaultCaptionsExpanded: true,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.lesson1Intro.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.lesson1Intro.endMs,
};

const m4Lesson2_Video: VideoActivityData = {
  id: "m4-l2-video",
  type: "video",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  continueButtonLabel: "Continue",
  defaultCaptionsExpanded: true,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.lesson2Intro.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.lesson2Intro.endMs,
};

const m4Step2_MCQ: MCQActivityData = {
  id: "m4-step2-mcq",
  type: "mcq",
  question: "I don't like it",
  options: [
    { choice: "No me gusta", correct: true },
    {
      choice: "Me gusta",
      correct: false,
      feedback:
        "'Me gusta' is affirmative. It needs a negative marker to express 'not'.",
    },
    {
      choice: "No tengo",
      correct: false,
      feedback: "'Tengo' comes from the verb 'tener' which means 'to have'.",
    },
    {
      choice: "Buenos días",
      correct: false,
      feedback: "'Buenos días' means 'Good morning'.",
    },
  ],
  isReverse: true,
};

const m4Step3_Fill: FillInTheBlankData = {
  id: "m4-step3-fill",
  type: "fill_in_the_blank",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.meGusta.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.meGusta.endMs,
  spanishSentence: "Este vestido sí ___ gusta",
  englishHint: "I do like this dress.",
  correctWord: BASIC_PHRASES_FILL_WORDS[0], // me
  correctFeedback:
    "'Me' is used when you're the object receiving the action. 'Me gusta' is like 'it pleases me'.",
  incorrectWord: BASIC_PHRASES_FILL_WORDS[1], // no
  extraIncorrectWords: [
    BASIC_PHRASES_FILL_WORDS[3],
    BASIC_PHRASES_FILL_WORDS[4],
  ],
  incorrectFeedback: {
    no: "'No' makes a phrase negative. In this sentence, the speaker is saying they like the dress, so 'no' is incorrect.",
    yo: "'Yo' is used when you're the subject performing the action, like 'yo tengo'.",
    mi: "'Mi' shows ownership or possession, like 'my'. It goes before a noun.",
  },
  preserveIncorrectAttempts: true,
};

const m4Step4_MCQ: MCQActivityData = {
  id: "m4-step4-mcq",
  type: "mcq",
  question: "Tampoco me gusta",
  correctFeedback:
    "'I don't like it either' is correct. 'Neither' or 'not either' in Spanish is 'tampoco'. It agrees with a negative.",
  options: [
    { choice: "I don't like it either", correct: true },
    {
      choice: "I like it too",
      correct: false,
      feedback:
        "'Too' in Spanish is 'también'. It agrees with something positive.",
    },
    {
      choice: "I like it a bit",
      correct: false,
      feedback:
        "'A bit' or 'a little' in Spanish is 'un poco'. It talks about amount.",
    },
    {
      choice: "I like it more",
      correct: false,
      feedback: "'More' in Spanish is 'más'. It talks about amount.",
    },
  ],
};

const m4Step5_Comprehension: MCQActivityData = {
  id: "m4-step5-comprehension",
  type: "mcq",
  question: "What does the character realize when she wakes up?",
  mcqLayout: "question_title_stacked",
  correctFeedback:
    "She has a party to go to! The word for party in Spanish is 'fiesta'.",
  options: [
    { choice: "She has a party to go to", correct: true },
    { choice: "She's late for work", correct: false },
    { choice: "Her phone is ringing", correct: false },
    { choice: "She woke up too early", correct: false },
  ],
};

const m4Lesson1_BuenosDiasMCQ: MCQActivityData = {
  id: "m4-l1-buenos-dias-mcq",
  type: "mcq",
  question: "Buenos días",
  correctFeedback: "'Buenos días' is the greeting you use for 'Good morning'.",
  options: [
    { choice: "Good morning", correct: true },
    {
      choice: "Good night",
      correct: false,
      feedback: "'Good night' is 'buenas noches'.",
    },
    {
      choice: "Good afternoon",
      correct: false,
      feedback: "'Good afternoon' is 'buenas tardes'.",
    },
    {
      choice: "Goodbye",
      correct: false,
      feedback: "'Goodbye' is 'adiós'.",
    },
  ],
};

const m4Step6_Celebrate: CelebrateData = {
  id: "m4-step6-celebrate",
  type: "celebrate",
  title: "Nice work!",
  knownItemsLabel: "3 phrases",
  knownItemsSubLabel: "Learned",
  patternLabel: "Negation",
  patternSubLabel: "no + tampoco",
  claimButtonLabel: "Claim Phrases!",
  claimPhrases: [
    {
      text: "Me gusta",
      translation: "I like it",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
    {
      text: "No me gusta",
      translation: "I don't like it",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
    {
      text: "Tampoco me gusta",
      translation: "I don't like it either",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
  ],
  words: [
    BASIC_PHRASES_1_WORDS[3], // Me gusta
    BASIC_PHRASES_1_WORDS[4], // No me gusta
    BASIC_PHRASES_1_WORDS[5], // Tampoco
  ],
};

const m4Lesson1_YoQuieroFill: FillInTheBlankData = {
  id: "m4-l1-yo-quiero-fill",
  type: "fill_in_the_blank",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.yoQuiero.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.yoQuiero.endMs,
  spanishSentence: "Yo ___ usar este vestido",
  englishHint: "I want to wear this dress.",
  correctWord: BASIC_PHRASES_FILL_WORDS[11], // Quiero
  correctFeedback:
    "'Quiero' means 'I want'. After 'yo', it tells us what the speaker wants to do.",
  incorrectWord: BASIC_PHRASES_1_WORDS[1], // Necesito
  extraIncorrectWords: [
    BASIC_PHRASES_FILL_WORDS[10], // Tengo
    BASIC_PHRASES_1_WORDS[3], // Me gusta
  ],
  incorrectFeedback: {
    necesito:
      "'Necesito' means 'I need'. Here she is choosing what she wants to wear.",
    tengo: "'Tengo' means 'I have', not 'I want'.",
    "me-gusta":
      "'Me gusta' means 'I like it'. The sentence needs the verb for 'I want'.",
  },
  preserveIncorrectAttempts: true,
};

const m4Step7_Build: BuildSentenceData = {
  id: "m4-step7-build",
  type: "build_sentence",
  correctSentence: "Así sí puedo ir a la fiesta",
  englishHint: "Listen to the video and build the full sentence.",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.asiSiPuedoIr.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.asiSiPuedoIr.endMs,
  words: ["fiesta", "puedo", "Así", "a", "ir", "sí", "la"],
  cognateWord: BASIC_PHRASES_LISTEN_WORDS[0],
  incorrectSentenceFeedback: {
    "Sí así puedo ir a la fiesta":
      "'Así sí' works together here: 'así' means 'this way' or 'like this', and 'sí' confirms 'yes'. The character is saying 'this way, yes, I can go'...",
    "Así sí ir puedo a la fiesta":
      "'Puedo' needs the action word right after it. In Spanish, use the conjugated helper first, then the unchanged verb: 'puedo ir' = 'I can go'.",
    "Así sí puedo a ir la fiesta":
      "'Ir a' is the Spanish pattern for going to a place. Keep 'a' after 'ir', then use 'la fiesta' for 'the party': 'ir a la fiesta'.",
  },
  incorrectAttemptHintAfter: 1,
  incorrectAttemptHint: "Psst! Here's a hint:\n\nAsí sí puedo ir a la fiesta",
  preserveIncorrectAttempts: true,
  disableVoiceNoteHint: true,
  showEnglishHints: true,
  wordHints: {
    así: "this way",
    sí: "yes",
    puedo: "I can",
    ir: "go",
    a: "to",
    la: "the",
    fiesta: "party",
  },
};

const m4Step8_Fill: FillInTheBlankData = {
  id: "m4-step8-fill",
  type: "fill_in_the_blank",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.dondeEsta.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.dondeEsta.endMs,
  spanishSentence: "¿Dónde ___ mi teléfono?",
  englishHint: "Where is my phone?",
  correctWord: BASIC_PHRASES_FILL_WORDS[7], // está
  correctFeedback:
    "'Está' comes from 'estar'. Use 'estar' for location or how something is right now.",
  incorrectWord: BASIC_PHRASES_FILL_WORDS[8], // es
  extraIncorrectWords: [
    BASIC_PHRASES_FILL_WORDS[9],
    BASIC_PHRASES_FILL_WORDS[6],
  ],
  incorrectFeedback: {
    es: "'Es' comes from 'ser'. Use 'ser' for identity or general traits. Use 'estar' for location or a current state.",
    estoy:
      "'Estoy' also comes from 'estar', but it is the 'I am' form. Here, the phone is the thing being located, making the subject 'it'.",
  },
  preserveIncorrectAttempts: true,
};

const m4Step9_MCQ: MCQActivityData = {
  id: "m4-step9-mcq",
  type: "mcq",
  question: "I don't have",
  correctFeedback:
    "'No tengo' means 'I don't have'. 'Tengo' is the first-person singular conjugation of the verb 'tener' ('to have'), and 'no' negates the phrase.",
  options: [
    { choice: "No tengo", correct: true },
    {
      choice: "Tengo",
      correct: false,
      feedback: "'Tengo' means 'I have'. Add 'no' to the phrase to negate it.",
    },
    {
      choice: "No tener",
      correct: false,
      feedback:
        "'Tener' is the infinitive form of the verb: 'to have'. When the subject is 'I', the correct conjugation is 'tengo'.",
    },
    {
      choice: "No me gusta",
      correct: false,
      feedback:
        "'No me gusta' means 'I don't like it'. It uses the verb 'gustar' meaning 'to like'.",
    },
  ],
  isReverse: true,
};

const m4Step10_NecesitoFill: FillInTheBlankData = {
  id: "m4-step10-necesito-fill",
  type: "fill_in_the_blank",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.necesito.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.necesito.endMs,
  spanishSentence: "___ cambiarme de ropa",
  englishHint: "I need to change clothes.",
  correctWord: BASIC_PHRASES_1_WORDS[1], // Necesito
  incorrectWord: BASIC_PHRASES_FILL_WORDS[11], // Quiero
  extraIncorrectWords: [
    BASIC_PHRASES_FILL_WORDS[10], // Tengo
    BASIC_PHRASES_1_WORDS[3], // Me gusta
  ],
  correctFeedback:
    "'Necesito' is the first person singular conjugation of the verb 'necesitar' which means 'to need'.",
  incorrectFeedback: {
    quiero:
      "Not quite! 'Quiero' means 'I want'. She is saying what she **NEEDS** to do.",
    tengo:
      "Not quite! 'Tengo' means 'I have'. She is saying what she **NEEDS** to do.",
    "me-gusta":
      "Not quite! 'Me gusta' means 'I like it'. She is saying what she **NEEDS** to do.",
  },
  preserveIncorrectAttempts: true,
};

const m4Step11_Comprehension: MCQActivityData = {
  id: "m4-step11-comprehension",
  type: "mcq",
  question:
    "What was the first thing she had to do to get ready for the party?",
  mcqLayout: "question_title_stacked",
  correctFeedback: "Right, 'cambiarme de ropa' means 'to change my clothes'.",
  options: [
    { choice: "Change her clothes", correct: true },
    { choice: "Do her hair", correct: false },
    { choice: "Buy new clothes", correct: false },
    { choice: "Find her shoes", correct: false },
  ],
};

const m4Lesson2_AquiEstaMCQ: MCQActivityData = {
  id: "m4-l2-aqui-esta-mcq",
  type: "mcq",
  question: "Aquí está mi teléfono",
  correctFeedback:
    "'Aquí está' means 'here is'. She found the phone she was looking for.",
  options: [
    { choice: "Here is my phone", correct: true },
    {
      choice: "Where is my phone?",
      correct: false,
      feedback:
        "'Where is...?' is '¿Dónde está...?' The word 'aquí' means 'here'.",
    },
    {
      choice: "I don't have my phone",
      correct: false,
      feedback: "'No tengo' means 'I don't have'. This line says she found it.",
    },
    {
      choice: "Here are my shoes",
      correct: false,
      feedback: "'Teléfono' means 'phone', not 'shoes'.",
    },
  ],
};

const m4Step12_Celebrate: CelebrateData = {
  id: "m4-step12-celebrate",
  type: "celebrate",
  title: "Nice work!",
  knownItemsLabel: "4 phrases",
  knownItemsSubLabel: "Learned",
  patternLabel: "Where is...?",
  patternSubLabel: "¿Dónde está...?",
  claimButtonLabel: "Claim Phrases!",
  claimPhrases: [
    {
      text: "Aquí está mi teléfono",
      translation: "Here is my phone",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
    {
      text: "¿Dónde está mi teléfono?",
      translation: "Where is my phone?",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
    {
      text: "No tengo",
      translation: "I don't have",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
    {
      text: "Necesito cambiarme de ropa",
      translation: "I need to change clothes",
      status: "learning",
      lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
    },
  ],
  words: [
    BASIC_PHRASES_1_WORDS[7], // Aquí está
    BASIC_PHRASES_1_WORDS[1], // Necesito
    BASIC_PHRASES_1_WORDS[6], // ¿Dónde está?
    BASIC_PHRASES_1_WORDS[8], // No tengo
  ],
};

const m4Lesson1PhraseMatch: MatchData = {
  id: "m4-l1-phrase-match",
  type: "match",
  pairs: [
    BASIC_PHRASES_1_WORDS[0], // Buenos días
    BASIC_PHRASES_1_WORDS[1], // Necesito
    BASIC_PHRASES_1_WORDS[2], // Yo quiero
    BASIC_PHRASES_1_WORDS[3], // Me gusta
    BASIC_PHRASES_1_WORDS[4], // No me gusta
  ],
};

const m4Step13_Match: MatchData = {
  id: "m4-step13-match",
  type: "match",
  pairs: [
    BASIC_PHRASES_1_WORDS[0], // Buenos días
    BASIC_PHRASES_1_WORDS[1], // Necesito
    BASIC_PHRASES_1_WORDS[2], // Yo quiero
    BASIC_PHRASES_1_WORDS[6], // ¿Dónde está?
    BASIC_PHRASES_1_WORDS[8], // No tengo
  ],
};

const m4Lesson2SpellTengo = createWriteActivity(
  "m4-l2-spell-tengo",
  "I have",
  "tengo",
);

const m4Lesson2PhraseMatch: MatchData = {
  id: "m4-l2-phrase-match",
  type: "match",
  pairs: [
    BASIC_PHRASES_1_WORDS[6], // ¿Dónde está?
    BASIC_PHRASES_1_WORDS[7], // Aquí está
    BASIC_PHRASES_1_WORDS[8], // No tengo
    BASIC_PHRASES_1_WORDS[1], // Necesito
    BASIC_PHRASES_FILL_WORDS[10], // Tengo
  ],
};

const m4Step14_Build: BuildSentenceData = {
  id: "m4-step14-build",
  type: "build_sentence",
  correctSentence: "No tengo zapatos",
  englishHint: "Listen to the video and build the full sentence.",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  videoStartMs: BASIC_PHRASES_1_VIDEO.segments.zapatos.startMs,
  videoEndMs: BASIC_PHRASES_1_VIDEO.segments.zapatos.endMs,
  words: ["No", "tengo", "zapatos"],
  cognateWord: BASIC_PHRASES_1_WORDS[8], // No tengo
  clearIncorrectAttempt: true,
  disableVoiceNoteHint: true,
  showEnglishHints: true,
  wordHints: { tengo: "I have", zapatos: "shoes" },
};

const m4Step15_MCQ: MCQActivityData = {
  id: "m4-step15-mcq",
  type: "mcq",
  question: "Se me olvidó mi billetera",
  questionBubbleWidth: 300,
  correctFeedback:
    "'Se me olvidó' is a pronominal construction Spanish uses when something slipped your mind.",
  options: [
    { choice: "I forgot my wallet", correct: true },
    {
      choice: "I remembered my wallet",
      correct: false,
      feedback:
        "'Olvidó' comes from the verb 'olvidar', which means 'to forget'.",
    },
    {
      choice: "I forgot my phone",
      correct: false,
      feedback: "'Billetera' is a noun meaning 'wallet'.",
    },
    {
      choice: "I lost my purse",
      correct: false,
      feedback:
        "'Olvidó' comes from the verb 'olvidar', which means 'to forget'.",
    },
  ],
};

const m4Step16_Synthesis: MCQActivityData = {
  id: "m4-step16-synthesis",
  type: "mcq",
  question: "Which item did she NOT talk about in the video?",
  correctFeedback: "There was no mention of a frisbee.",
  mcqLayout: "question_title_centered_grid",
  persistCorrectButtonState: true,
  options: [
    { choice: "Her frisbee", correct: true, icon: { image: "frisbee" } },
    { choice: "Her dress", correct: false, icon: { image: "dress" } },
    { choice: "Her shoes", correct: false, icon: { image: "shoes" } },
    { choice: "Her phone", correct: false, icon: { image: "phone" } },
  ],
};

const m4Step17_Video: VideoActivityData = {
  id: "m4-step17-video",
  type: "video",
  lessonId: BASIC_PHRASES_1_VIDEO.fullStoryLessonId,
  chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
  continueButtonLabel: "Continue",
  defaultCaptionsExpanded: true,
};

const m4Step18_Celebrate: CelebrateData = {
  id: "m4-step18-celebrate",
  type: "celebrate",
  title: "Chapter Complete!",
  isFinal: true,
  words: BASIC_PHRASES_1_WORDS,
  patternLabels: ["9 essential phrases"],
  showActivationProgress: true,
  listeningTimeLessonIds: [BASIC_PHRASES_1_VIDEO.fullStoryLessonId],
};

const m4Lesson1Complete: CelebrateData = {
  id: "m4-l1-complete",
  type: "celebrate",
  title: "Lesson 1 Complete!",
  isLessonComplete: true,
  knownItemsLabel: "5 phrases",
  knownItemsSubLabel: "Learned",
  patternLabel: "Getting ready",
  patternLabels: ["5 phrases"],
  showActivationProgress: true,
  listeningTimeLessonIds: [BASIC_PHRASES_1_VIDEO.fullStoryLessonId],
  words: [
    BASIC_PHRASES_1_WORDS[0], // Buenos días
    BASIC_PHRASES_1_WORDS[1], // Necesito
    BASIC_PHRASES_1_WORDS[2], // Yo quiero
    BASIC_PHRASES_1_WORDS[3], // Me gusta
    BASIC_PHRASES_1_WORDS[4], // No me gusta
    BASIC_PHRASES_1_WORDS[5], // Tampoco
    BASIC_PHRASES_LISTEN_WORDS[0], // Así sí puedo ir a la fiesta
  ],
};

const basicPhrases1Lesson1Activities = [
  m4Step1_Video,
  m4Step5_Comprehension,
  m4Lesson1_BuenosDiasMCQ,
  m4Step2_MCQ,
  m4Step3_Fill,
  m4Step4_MCQ,
  m4Step6_Celebrate,
  m4Step10_NecesitoFill,
  m4Step11_Comprehension,
  m4Lesson1_YoQuieroFill,
  m4Step7_Build,
  m4Lesson1PhraseMatch,
  m4Lesson1Complete,
];

const basicPhrases1Lesson2Activities = [
  m4Lesson2_Video,
  m4Step14_Build,
  m4Step9_MCQ,
  m4Step8_Fill,
  m4Lesson2_AquiEstaMCQ,
  m4Step12_Celebrate,
  m4Step13_Match,
  m4Lesson2SpellTengo,
  m4Step15_MCQ,
  m4Step16_Synthesis,
  m4Lesson2PhraseMatch,
  m4Step17_Video,
  m4Step18_Celebrate,
];

// =============================================================================
// Module 4 Definition - Basic Phrases 1
// =============================================================================

export const MODULE_4: Module = {
  id: "basic-phrases-1",
  name: "Basic Phrases 1: La Fiesta",
  patterns: ["COMMON"],
  chapterOverview: {
    youtubeId: BASIC_PHRASES_1_VIDEO.youtubeId,
    aspectRatio: "9:16",
    chapterId: BASIC_PHRASES_1_VIDEO.chapterId,
    lessons: [
      {
        id: "bp1-lesson-1",
        title: "Getting Ready",
        startActivityId: m4Step1_Video.id,
        backendLessonId: 45789,
      },
      {
        id: "bp1-lesson-2",
        title: "Finding What's Missing",
        startActivityId: m4Lesson2_Video.id,
        backendLessonId: 57461,
      },
    ],
  },
  activities: [
    ...basicPhrases1Lesson1Activities,
    ...basicPhrases1Lesson2Activities,
  ],
};
